import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import type { AppDocument, SheetContent, SheetPayload } from './types';

export const apiDocsKeys = {
  all: ['api-docs'] as const,
  sheets: ['api-docs', 'sheets'] as const,
  sheet: (id: string) => ['api-docs', 'sheet', id] as const,
  documents: ['api-docs', 'documents'] as const,
  document: (id: string) => ['api-docs', 'document', id] as const,
};

/** Pulls `{ message }` out of an axios error, falls back to a default text. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

/* ---------------- Who can manage ---------------- */

export function useApiDocsManager() {
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);
  const isSuper = role === USER_ROLES.S_ADMIN;
  const canManage = isSuper || role === USER_ROLES.SUB_SUPER_ADMIN;
  /** Superadmin edits anything; a sub-superadmin only rows he created. */
  const canModify = (ownerId: number) => isSuper || (canManage && userId !== null && ownerId === Number(userId));
  return { isSuper, canManage, userId, canModify };
}

/** Management pages: anyone who is not super / sub-super admin goes home (same as old_obd). */
export function useRequireApiDocsManager() {
  const { canManage } = useApiDocsManager();
  const role = useAuthStore((s) => s.role);
  const navigate = useNavigate();
  useEffect(() => {
    if (role && !canManage) navigate('/', { replace: true });
  }, [role, canManage, navigate]);
  return canManage;
}

/* ---------------- Sheets ---------------- */

export function useSheets(enabled = true) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: apiDocsKeys.sheets,
    enabled: enabled && Boolean(userId),
    queryFn: () => api.get<SheetContent[]>(`/api/obd/sheet/content/list/${userId}`),
  });
}

/** Manager-only endpoint (used by view/edit pages). */
export function useSheet(id: string | undefined) {
  return useQuery({
    queryKey: apiDocsKeys.sheet(id ?? ''),
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      const res = await api.get<SheetContent | ''>(`/api/obd/sheet/content/${id}`);
      if (!res) throw new Error('Sheet not found');
      return res;
    },
  });
}

export function useSaveSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: SheetPayload) =>
      p.id
        ? api.post<{ message: string }>('/api/obd/sheet/content/update', p)
        : api.post<{ id: string; message: string }>('/api/obd/sheet/content/create', p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiDocsKeys.all });
    },
  });
}

export function useDeleteSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message: string }>('/api/obd/sheet/content/delete', { id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiDocsKeys.all });
    },
  });
}

/* ---------------- Documents ---------------- */

export function useDocuments(enabled = true) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: apiDocsKeys.documents,
    enabled: enabled && Boolean(userId),
    queryFn: () => api.get<AppDocument[]>(`/api/obd/document/list/${userId}`),
  });
}

export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: apiDocsKeys.document(id ?? ''),
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      const res = await api.get<AppDocument | ''>(`/api/obd/document/${id}`);
      if (!res) throw new Error('Document not found');
      return res;
    },
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.userId);
  return useMutation({
    mutationFn: (v: { file: File; documentName: string; documentType: string }) => {
      const form = new FormData();
      form.append('userId', String(userId ?? ''));
      form.append('documentName', v.documentName);
      form.append('documentType', v.documentType);
      form.append('documentFile', v.file);
      return api.post<{ id: string; message: string }>('/api/obd/document/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiDocsKeys.all });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message: string }>('/api/obd/document/delete', { id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiDocsKeys.all });
    },
  });
}

/** Fetches the raw file through axios so the Bearer token is sent. */
export async function fetchDocumentBlob(id: number): Promise<Blob> {
  const res = await apiClient.get(`/api/obd/document/download/${id}`, { responseType: 'blob' });
  return res.data as Blob;
}

export async function downloadDocument(id: number, fileName: string): Promise<void> {
  const blob = await fetchDocumentBlob(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
